import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  List,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { EmptyState } from "../EmptyState";
import { useAuth } from "../../lib/auth";
import type { MetricItem, TaskStatus } from "../../lib/types";

interface MetricFormState {
  title: string;
  description: string;
  dueDate: string;
  dependencies: string;
}

interface MetricsTabProps {
  metrics: MetricItem[];
  metricForm: MetricFormState;
  setMetricForm: React.Dispatch<React.SetStateAction<MetricFormState>>;
  editingMetric: MetricItem | null;
  metricModalOpen: boolean;
  onOpenNewMetric: () => void;
  onOpenEditMetric: (metric: MetricItem) => void;
  onSaveMetric: () => Promise<void> | void;
  onDeleteMetric: (metricId: string) => void | Promise<void>;
  onCloseMetricModal: () => void;
}

const isOverdue = (dueDate?: string | null, status?: TaskStatus) => {
  if (!dueDate) return false;
  if (status === "done") return false;
  return dayjs(dueDate).isBefore(dayjs(), "day");
};

export default function MetricsTab({
  metrics,
  metricForm,
  setMetricForm,
  editingMetric,
  metricModalOpen,
  onOpenNewMetric,
  onOpenEditMetric,
  onSaveMetric,
  onDeleteMetric,
  onCloseMetricModal,
}: MetricsTabProps) {
  const { isAdmin, canWrite } = useAuth();
  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title="Project Metrics"
        extra={
          canWrite ? (
            <Button type="primary" onClick={onOpenNewMetric} aria-label="Create new metric">
              New Metric
            </Button>
          ) : null
        }
      >
        {metrics.length === 0 ? (
          <EmptyState
            description="No metrics yet."
            action={
              canWrite ? { label: "New Metric", onClick: onOpenNewMetric } : undefined
            }
          />
        ) : (
          <List
            aria-label="Project metrics list"
            dataSource={metrics}
            renderItem={(metric) => (
              <List.Item
                actions={[
                  ...(canWrite
                    ? [
                        <Button
                          key="edit"
                          type="link"
                          aria-label={`Edit metric ${metric.title}`}
                          onClick={() => onOpenEditMetric(metric)}
                        >
                          Edit
                        </Button>,
                      ]
                    : []),
                  ...(isAdmin
                    ? [
                        <Button
                          key="delete"
                          danger
                          type="link"
                          aria-label={`Delete metric ${metric.title}`}
                          onClick={() => onDeleteMetric(metric.id)}
                        >
                          Delete
                        </Button>,
                      ]
                    : []),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Typography.Text>{metric.title}</Typography.Text>
                      {metric.completedAt && <Tag color="success">Completed</Tag>}
                      {metric.dueDate && isOverdue(metric.dueDate, "todo") && (
                        <Tag color="error">Overdue</Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Typography.Text type="secondary">
                        Due {metric.dueDate || "—"}
                      </Typography.Text>
                      {metric.dependencies && (
                        <Typography.Text type="secondary">
                          Dependencies: {metric.dependencies}
                        </Typography.Text>
                      )}
                      {metric.dependsOnTaskIds && metric.dependsOnTaskIds.length > 0 && (
                        <Typography.Text type="secondary">
                          Linked to {metric.dependsOnTaskIds.length} task
                          {metric.dependsOnTaskIds.length === 1 ? "" : "s"}
                        </Typography.Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      <Modal
        title={editingMetric ? "Edit Metric" : "New Metric"}
        open={metricModalOpen}
        onCancel={onCloseMetricModal}
        onOk={onSaveMetric}
        okText={editingMetric ? "Save" : "Create"}
        okButtonProps={{ disabled: !metricForm.title.trim() }}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Metric title" required>
            <Input
              value={metricForm.title}
              onChange={(event) =>
                setMetricForm((prev) => ({ ...prev, title: event.target.value }))
              }
            />
          </Form.Item>
          <Form.Item label="Description">
            <Input.TextArea
              value={metricForm.description}
              onChange={(event) =>
                setMetricForm((prev) => ({ ...prev, description: event.target.value }))
              }
              autoSize={{ minRows: 2 }}
            />
          </Form.Item>
          <Form.Item label="Dependencies">
            <Input
              value={metricForm.dependencies}
              onChange={(event) =>
                setMetricForm((prev) => ({ ...prev, dependencies: event.target.value }))
              }
            />
          </Form.Item>
          <Form.Item label="Due date">
            <DatePicker
              value={metricForm.dueDate ? dayjs(metricForm.dueDate) : null}
              onChange={(date) =>
                setMetricForm((prev) => ({
                  ...prev,
                  dueDate: date ? date.format("YYYY-MM-DD") : "",
                }))
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
